import { useState, useEffect } from 'react';
import { poaAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, FileText, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function PoaPage() {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await poaAPI.getTemplates();
        setTemplates(res.data);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    load();
  }, []);

  const handleDownload = (key, template) => {
    const content = `${template.title}\n${'='.repeat(60)}\n\nDate: ${new Date().toLocaleDateString()}\n\n--- ROOT CAUSE ANALYSIS ---\n${template.root_cause}\n\n--- CORRECTIVE ACTIONS TAKEN ---\n${template.corrective_actions}\n\n--- PREVENTIVE MEASURES ---\n${template.preventive_measures}\n\n--- SIGNATURE ---\n[Your Name]\n[Account Name]\n[Date]`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `POA-${key.replace(/[^a-zA-Z0-9]/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('POA template downloaded');
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" />{[1,2,3].map(i => <Skeleton key={i} className="h-40" />)}</div>;

  return (
    <div className="space-y-6" data-testid="poa-page">
      <div>
        <h1 className="text-xl font-semibold">Plan of Action (POA) Templates</h1>
        <p className="text-sm text-muted-foreground">Download ready-to-submit POA templates for Amazon policy violations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(templates).map(([key, template]) => (
          <Card key={key} className="bg-card border-border/80 hover:border-border transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[hsl(188,100%,55%)]" />
                  <CardTitle className="text-sm font-semibold">{template.title}</CardTitle>
                </div>
                <Button size="sm" variant="ghost" onClick={() => handleDownload(key, template)} className="h-8 px-3 text-xs gap-1.5"><Download className="w-3.5 h-3.5" /> Download</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/20 border border-border/40">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Root Cause</p>
                  <p className="text-xs text-foreground/80 whitespace-pre-line">{template.root_cause?.slice(0, 200)}...</p>
                </div>
                {expanded === key && (
                  <>
                    <div className="p-3 rounded-lg bg-muted/20 border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Corrective Actions</p>
                      <p className="text-xs text-foreground/80 whitespace-pre-line">{template.corrective_actions}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/20 border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Preventive Measures</p>
                      <p className="text-xs text-foreground/80 whitespace-pre-line">{template.preventive_measures}</p>
                    </div>
                  </>
                )}
                <button onClick={() => setExpanded(expanded === key ? null : key)} className="text-xs text-[hsl(188,100%,55%)] hover:underline">{expanded === key ? 'Show Less' : 'View Full Template'}</button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
